import {
  type MaintenanceWorkOrderListQuery,
  type MaintenanceWorkOrderStatus,
} from '@gb-ticket/shared-types';
import { useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { usePageTitle } from '@/hooks/use-page-title';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { MaintenanceOptionalSelect } from '@/products/maintenance/features/maintenance-filter-select';
import { MaintenanceListFooter } from '@/products/maintenance/features/maintenance-list-footer';
import { nextMaintenanceColumnSort } from '@/products/maintenance/features/maintenance-list-query';
import {
  maintenanceWorkOrderCustomerColumn,
  maintenanceWorkOrderDueColumn,
  maintenanceWorkOrderInstallationColumn,
  maintenanceWorkOrderScheduledColumn,
  maintenanceWorkOrderTechnicianColumn,
} from '@/products/maintenance/features/maintenance-work-order-list-columns';
import {
  isMaintenanceWorkOrderSortBy,
  MAINTENANCE_PLANNING_SORT_FIELDS,
  useRememberedWorkOrderSort,
  writeMaintenanceWorkOrderListSort,
} from '@/products/maintenance/features/maintenance-work-order-list-sort';
import { MaintenanceListToolbar } from '@/products/maintenance/features/maintenance-list-toolbar';
import { MaintenanceCustomerSearchFilter } from '@/products/maintenance/features/maintenance-master-data-filters';
import {
  maintenancePlanningPath,
  maintenancePlanningQueryFromSearch,
} from '@/products/maintenance/features/maintenance-planning-path';
import { MaintenanceRecordList } from '@/products/maintenance/features/maintenance-record-list';
import { useMaintenanceRowSelection } from '@/products/maintenance/features/maintenance-row-selection';
import { MAINTENANCE_WORK_ORDER_STATUS_OPTIONS } from '@/products/maintenance/features/maintenance-status';
import {
  useMaintenanceTechnicians,
  useMaintenanceWorkOrders,
} from '@/products/maintenance/features/work-orders/use-maintenance-work-orders';
import { MaintenanceWorkOrderSelectionBar } from '@/products/maintenance/features/work-orders/maintenance-work-order-selection-bar';
import {
  maintenancePlanningNewPath,
  maintenanceWorkOrderPath,
  MAINTENANCE_TRASH_PATH,
} from '@/products/maintenance/maintenance-paths';

export function MaintenancePlanningPage() {
  usePageTitle('Wartungsplanung');
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const parsedQuery = useMemo(
    () => maintenancePlanningQueryFromSearch(search.toString()),
    [search],
  );
  const query = useRememberedWorkOrderSort(
    search.toString(),
    'planning',
    MAINTENANCE_PLANNING_SORT_FIELDS,
    parsedQuery,
  );
  const list = useMaintenanceWorkOrders(query);
  const technicians = useMaintenanceTechnicians();
  const items = useMemo(() => list.data?.items ?? [], [list.data]);
  const visibleIds = useMemo(() => items.map((order) => order.id), [items]);
  const selection = useMaintenanceRowSelection(visibleIds);
  const extraCount = [query.scheduledFrom, query.scheduledTo, query.customerId].filter(
    Boolean,
  ).length;
  const filtered = Boolean(query.status) || Boolean(query.assignedToId) || extraCount > 0;

  function apply(patch: Partial<MaintenanceWorkOrderListQuery>) {
    void navigate(maintenancePlanningPath({ ...query, ...patch, page: patch.page ?? 1 }));
  }

  function resetFilters() {
    void navigate(
      maintenancePlanningPath({
        page: 1,
        pageSize: query.pageSize,
        sortBy: query.sortBy,
        sortOrder: query.sortOrder,
      }),
    );
  }

  function onColumnSort(sortBy: string) {
    if (!isMaintenanceWorkOrderSortBy(sortBy, MAINTENANCE_PLANNING_SORT_FIELDS)) {
      return;
    }
    const next = nextMaintenanceColumnSort(query, sortBy);
    writeMaintenanceWorkOrderListSort('planning', { sortBy, sortOrder: next.sortOrder });
    apply(next);
  }

  return (
    <div className="space-y-6">
      <PlatformPageHeader
        title="Planung"
        description="Wartungsaufträge filtern, anlegen und zuweisen. Entfernte Aufträge liegen im Papierkorb."
        action={
          <Button className="min-h-11" asChild>
            <Link to={maintenancePlanningNewPath()}>Auftrag anlegen</Link>
          </Button>
        }
      />

      <p className="text-sm">
        <Link className="text-primary hover:underline" to={MAINTENANCE_TRASH_PATH}>
          Papierkorb öffnen
        </Link>
      </p>

      <section className="space-y-3">
        <h2 className="text-base font-semibold">Aufträge</h2>
        <MaintenanceListToolbar
          extraCount={extraCount}
          filtered={filtered}
          onReset={resetFilters}
          extra={
            <>
              <div className="min-w-0 space-y-2">
                <Label htmlFor="plan-from">Termin von</Label>
                <Input
                  id="plan-from"
                  type="date"
                  className="min-h-11"
                  value={query.scheduledFrom ?? ''}
                  onChange={(event) => apply({ scheduledFrom: event.target.value || undefined })}
                />
              </div>
              <div className="min-w-0 space-y-2">
                <Label htmlFor="plan-to">Termin bis</Label>
                <Input
                  id="plan-to"
                  type="date"
                  className="min-h-11"
                  value={query.scheduledTo ?? ''}
                  onChange={(event) => apply({ scheduledTo: event.target.value || undefined })}
                />
              </div>
              <MaintenanceCustomerSearchFilter
                value={query.customerId ?? ''}
                onChange={(customerId) => apply({ customerId: customerId || undefined })}
              />
            </>
          }
        >
          <MaintenanceOptionalSelect
            label="Status"
            id="plan-status"
            value={query.status ?? ''}
            onValueChange={(status) =>
              apply({
                status: status ? (status as MaintenanceWorkOrderStatus) : undefined,
              })
            }
            items={MAINTENANCE_WORK_ORDER_STATUS_OPTIONS}
          />
          <MaintenanceOptionalSelect
            label="Monteur"
            id="plan-tech"
            value={query.assignedToId ?? ''}
            onValueChange={(assignedToId) => apply({ assignedToId: assignedToId || undefined })}
            items={
              technicians.data?.items.map((technician) => ({
                value: technician.id,
                label: technician.displayName,
              })) ?? []
            }
          />
        </MaintenanceListToolbar>
        <MaintenanceWorkOrderSelectionBar
          orders={items}
          selectedIds={selection.selectedIds}
          onClear={() => selection.clear()}
        />
        <MaintenanceRecordList
          rows={items}
          pending={list.isPending ? 'Aufträge werden geladen …' : undefined}
          emptyLabel="Keine Aufträge für diese Filter."
          filtered={filtered}
          onReset={resetFilters}
          getRowId={(order) => order.id}
          getHref={(order) => maintenanceWorkOrderPath(order.id)}
          getTitle={(order) => order.orderNumber}
          getMeta={(order) =>
            `${order.customer.name} · ${order.installation.name} · ${order.assignedTo.displayName}`
          }
          getStatus={(order) => order.status}
          selection={selection}
          titleHeader="Auftrag"
          titleSortKey="orderNumber"
          statusSortKey="status"
          sort={{
            sortBy: query.sortBy,
            sortOrder: query.sortOrder,
            onSort: onColumnSort,
          }}
          columns={[
            maintenanceWorkOrderCustomerColumn(),
            maintenanceWorkOrderInstallationColumn(),
            maintenanceWorkOrderTechnicianColumn(),
            maintenanceWorkOrderScheduledColumn(),
            maintenanceWorkOrderDueColumn(),
          ]}
        />
        {items.length > 0 && list.data ? (
          <MaintenanceListFooter
            page={list.data.page}
            pageSize={list.data.pageSize}
            total={list.data.total}
            onPageChange={(page) => apply({ page })}
          />
        ) : null}
      </section>
    </div>
  );
}
